<template>
  <div v-if="hasRoomRates" class="be-room-card">
    <transition name="fade">
      <div v-if="expanded_rate_id !== null" class="be-room-card__backdrop" @click="closeRateDetail"></div>
    </transition>

    <div class="be-room-card__grid">
      <RoomCardSidebar :type="type" @open-detail="openDetail" />

      <div class="be-room-card__rates">
        <div class="be-room-card__rates-head">
          <div>Benefits</div>
          <div class="text-center">Guest</div>
          <div class="text-center">Total {{ $capitalize(type.type) }}</div>
          <div class="text-center">{{ $t('general.price') }} {{ nightCount }} {{ $pluralWord(nightCount, 'night') }}</div>
        </div>

        <RoomCardRateRow
          v-for="row in rateRows"
          :key="row.rate.id"
          class="be-anim-fade-up be-anim-fade-up--fast"
          :style="{ animationDelay: row.delay }"
          :rate="row.rate"
          :type="type"
          :avail="row.avail"
          :max-qty="row.maxQty"
          :selected-qty="row.selectedQty"
          :is-detail-shown="row.isDetailShown"
          :show-rooms-left="row.showRoomsLeft"
          :room-left-text="row.roomLeftText"
          @open-rate-detail="openRateDetail(row.rate)"
          @select-qty="selectRoomQty(row.rate, $event)"
          @open-qty-modal="openQtyModal(row.rate)"
          @book-now="bookNow(row.rate)"
          @add-to-cart="addToCartOne(row.rate)" />

        <div v-if="hasMoreRates" class="be-room-card__showmore be-press" @click="toggleShowMoreRates">
          {{ rates_more ? $t('hotel.show-more-rates') : $t('hotel.show-less') }}
        </div>
      </div>
    </div>

    <RoomCardQtyModal
      :rate="qty_modal_rate"
      :type="type"
      :max-qty="qty_modal_max"
      :initial-qty="qty_modal_initial"
      :room-left-text="qty_modal_room_left"
      @close="qty_modal_rate = null"
      @submit="submitQtyModal" />

    <RoomCardConfirmModal
      v-model="show_book_now_confirm"
      @cancel="book_now_candidate = null"
      @confirm="confirmBookNow(book_now_candidate)" />
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex';
  import { trackEvent } from '@/utils/analytics';
  import RoomCardSidebar from './room-card/RoomCardSidebar.vue';
  import RoomCardRateRow from './room-card/RoomCardRateRow.vue';
  import RoomCardQtyModal from './room-card/RoomCardQtyModal.vue';
  import RoomCardConfirmModal from './room-card/RoomCardConfirmModal.vue';
  import { generateRoomRateGuestParam } from './room-card/guestAllocation.js';

  const RATE_PREVIEW_COUNT = 5;
  /* Rows cascade in. Restarting the delay every preview batch keeps the rows
     revealed by "show more" cascading too instead of landing all at once. */
  const RATE_STAGGER_STEP_MS = 45;

  export default {
    name: 'BeRoomCard',
    components: {
      RoomCardSidebar,
      RoomCardRateRow,
      RoomCardQtyModal,
      RoomCardConfirmModal
    },
    props: {
      type: {
        type: Object,
        default: () => ({})
      }
    },
    data(){
      return {
        length_more: RATE_PREVIEW_COUNT,
        rates_more: true,
        selected_qty: {},
        book_now_candidate: null,
        qty_modal_rate: null,
        expanded_rate_id: null
      }
    },
    computed: {
      show_book_now_confirm: {
        get(){
          return this.book_now_candidate !== null;
        },
        set(isShown){
          if(!isShown) this.book_now_candidate = null;
        }
      },
      qty_modal_max(){
        if(this.qty_modal_rate === null) return 1;
        return this.maxSelectableQty(this.qty_modal_rate);
      },
      qty_modal_initial(){
        if(this.qty_modal_rate === null) return 1;
        return this.getSelectedQty(this.qty_modal_rate);
      },
      qty_modal_room_left(){
        if(this.qty_modal_rate === null) return '';
        return this.printRoomLeft(this.roomAvail(this.qty_modal_rate));
      },
      nightCount(){
        const { check_in, check_out } = this.getBeFilter().date;
        return this.$countDays(check_in, check_out);
      },
      roomRates(){
        return this.type.room_rate || [];
      },
      // sort by promotion can return a room type without any promo rate
      hasRoomRates(){
        return this.roomRates.length > 0;
      },
      hasMoreRates(){
        return this.roomRates.length > RATE_PREVIEW_COUNT;
      },
      rateRows(){
        return this.roomRates.slice(0, this.length_more).map((rate, index) => {
          const avail = this.roomAvail(rate);
          return {
            rate,
            avail,
            delay: `${(index % RATE_PREVIEW_COUNT) * RATE_STAGGER_STEP_MS}ms`,
            maxQty: this.maxSelectableQty(rate),
            selectedQty: this.getSelectedQty(rate),
            isDetailShown: this.expanded_rate_id === rate.id,
            showRoomsLeft: this.shouldShowRoomsLeft(avail),
            roomLeftText: this.printRoomLeft(avail)
          };
        });
      }
    },
    methods: {
      ...mapGetters(['getBeSortBy','getBeFilter','getBeCartRoom','getMerchant','getCurrentLanguage']),
      ...mapMutations(['BeManipulateRoomRate','changeBeRoomAvbl','beRemoveRoomRate','BeManipulateRoomRateGuest']),
      openDetail(){
        this.onBack('room_type_detail', this.type);
        this.onBack('handlePopupFilter', 'detail-room');
        this.trackRoomDetailClick(this.type);
        this.hitGoogleAnalytics('room_detail');
      },
      /* the detail is an inline accordion, so it must not raise the page backdrop —
         that overlay is for the legacy mobile popups and would cover the card */
      openRateDetail(data){
        if(this.expanded_rate_id === data.id){
          this.closeRateDetail();
          return;
        }
        this.expanded_rate_id = data.id;
        this.sendMetaCapi(data);
        this.trackAnalitycs(data);
      },
      closeRateDetail(){
        this.expanded_rate_id = null;
      },
      roomAvail(data){
        return this.$roomAvbl(data.room_type.id, data.id);
      },
      /* $roomAvbl already adds this rate's own cart qty back into the pool,
         so adding getQty() on top would let a 1-room type offer 2 */
      maxSelectableQty(data){
        return this.roomAvail(data);
      },
      selectRoomQty(data, qty){
        this.$set(this.selected_qty, data.id, parseInt(qty));
      },
      // a selection made earlier can outlive the availability a sibling rate consumed
      getSelectedQty(rate){
        const chosen = this.selected_qty[rate.id] || this.getQty(rate.id) || 1;
        return Math.min(chosen, Math.max(this.maxSelectableQty(rate), 1));
      },
      openQtyModal(data){
        this.qty_modal_rate = data;
      },
      submitQtyModal({ action, qty }){
        const data = this.qty_modal_rate;
        this.$set(this.selected_qty, data.id, qty);
        this.qty_modal_rate = null;
        if(action === 'book') this.bookNow(data);
        else this.addToCartOne(data);
      },
      addToCartOne(data){
        const qty = this.getSelectedQty(data);
        this.toggleBookingRateCart('toggleBookingRateCart', 'add', data, qty);
        this.addRoomRate(qty, data);
      },
      bookNow(data){
        const hasOtherRates = this.getBeCartRoom().some(item => item.room_rate.id !== data.id);
        if(hasOtherRates){
          this.book_now_candidate = data;
          return;
        }
        this.confirmBookNow(data);
      },
      confirmBookNow(data){
        const qty = this.getSelectedQty(data);
        this.clearCartRates();
        this.addRoomRate(qty, data);
        this.book_now_candidate = null;
        this.$router.push(`/${this.getMerchant().code}/cart`);
      },
      // Book Now must land on the cart with only the chosen rate selected
      clearCartRates(){
        this.getBeCartRoom().slice().forEach(item => {
          this.beRemoveRoomRate(item.room_rate.id);
          this.updateRoomTypeAvailability(item.qty, item.room_rate, 'inc');
        });
      },
      printRoomLeft(qty){
        if(this.getCurrentLanguage() == 'en' && qty == 1){
          return `Only ${qty} ${this.$t('hotel.unit-type.'+this.type.type+'.unit')} left`
        }else{
          return `Only ${qty} ${this.$t('hotel.unit-type.'+this.type.type+'.units')} left`
        }
      },
      shouldShowRoomsLeft(avail){
        const indicator = this.getMerchant().room_availability_max_show
        const isNotShow = indicator == 0
        const isShowAll = indicator == null
        const isShowLimited = avail <= indicator

        if(isNotShow || avail == 0) return false
        return Boolean(isShowAll || isShowLimited)
      },
      trackRoomDetailClick(item){
        const label = this.getBeSortBy() == 'room_type' ? 'Clicked Room Type Detail' : 'Clicked Rate Detail'
        this.$track('view_item', { item_id: item.id, item_name: item.name, label })
      },
      onBack(type,data1,data2){
        this.$emit(type,data1,data2)
      },
      hitGoogleAnalytics(event){
        if(this.$hasGoogleTag()){
          this.$gtag('event',event, { method: 'Google' })
        }
      },
      toggleBookingRateCart(button, type, data, qty = 1){
        this.$emit(button,type,data,qty)
      },
      updateRoomTypeAvailability(qty, currentRoomRate, action){
        const {
          id: rr_id,
          room_type: { id, is_virtual, availability_affected }
        } = currentRoomRate
        const availabilityParams = {
          id,
          rr_id,
          is_virtual,
          availability_affected,
          action,
          qty
        }
        this.changeBeRoomAvbl(availabilityParams);
      },
      addRoomRate(qty, selectedRoomRate){
        const validQty = parseInt(qty);
        const currentRoomRate = this.$deepCopy(selectedRoomRate)

        const savedRoom = this.$deepCopy(this.getBeCartRoom())
        const savedRoomRateIndex = savedRoom.findIndex(savedRoomRate=>savedRoomRate.room_rate.id === currentRoomRate.id)

        if(savedRoomRateIndex != -1){
          this.beRemoveRoomRate(currentRoomRate.id);
          this.updateRoomTypeAvailability(savedRoom[savedRoomRateIndex].qty, currentRoomRate, 'inc')
          setTimeout(()=>{
            this.addRoomRate(validQty, currentRoomRate)
          },100)
        } else {
          this.updateRoomTypeAvailability(validQty, currentRoomRate, 'dec')
          const roomRateParam = {
            room_rate: currentRoomRate,
            qty: validQty,
            guest: generateRoomRateGuestParam(validQty, currentRoomRate, this.getBeFilter().guest, this.getBeCartRoom())
          }
          this.BeManipulateRoomRate(roomRateParam)
          setTimeout(()=>{
            this.updatePriceAccordingToPax(currentRoomRate.id, roomRateParam.guest)
          },100)

          this.$sendEventMetaConversionsAPI('AddToWishlist', {
            id: currentRoomRate.id_ori,
            name: currentRoomRate.name,
            dates: currentRoomRate.dates,
            qty: validQty
          });
          trackEvent("add_to_cart", {roomRate: currentRoomRate, qty: validQty})
        }
      },
      updatePriceAccordingToPax(room_rate_id,guests){
        const guest_params = {
          id: room_rate_id,
          guest: {
            included: [],
            extra: []
          }
        }

        guests.forEach(item=>{
          guest_params.guest.included.push(item.included)
          guest_params.guest.extra.push(item.extra)
        })

        this.BeManipulateRoomRateGuest(guest_params)
      },
      toggleShowMoreRates(){
        if(this.rates_more){
          this.length_more = this.type.room_rate.length
          this.rates_more = false
        }else{
          this.length_more = RATE_PREVIEW_COUNT
          this.rates_more = true
        }
      },
      getQty(id){
        let display = 0
        if(this.getBeCartRoom().length > 0){
          const record = this.getBeCartRoom().findIndex(x=>x.room_rate.id == id)
          if(record != -1){
            display = this.getBeCartRoom()[record].qty
          }
        }
        return display
      },
      sendMetaCapi(data){
        setTimeout(() => {
          if(this.expanded_rate_id !== data.id) return;

          this.$sendEventMetaConversionsAPI('ViewContent', {
            id: data.id_ori,
            name: data.name,
            dates: data.dates
          }, 'Room Rate');
        }, 100);
      },
      trackAnalitycs(data=null){
        trackEvent('view_item', data);
      }
    }
  }
</script>

<style scoped>
.be-room-card {
  /* type ramp — inherited by room-card/* children (portaled modals repeat these values) */
  --be-v3-text-caption: 0.75rem;
  --be-v3-text-secondary: 0.8125rem;
  --be-v3-text-body: 0.875rem;
  --be-v3-text-subhead: 0.9375rem;
  --be-v3-text-heading: 1.125rem;
  --be-v3-text-price: 1.25rem;
  /* border ramp — inherited by room-card/* children */
  --be-v3-border: #d5d9e0;
  --be-v3-border-soft: #e3e6eb;
  /* single source of truth for the rates table tracks, shared with the head row and
     RoomCardRateRow. Px values are the floors that keep each cell readable when the
     card only has ~75% of a 992px viewport; the fr ratios take over above that. */
  --be-v3-rate-grid: minmax(0, 2.2fr) minmax(72px, 0.6fr) minmax(78px, 0.6fr) minmax(158px, 1.4fr);
  background: #f4f5f7;
  border: 1px solid var(--be-v3-border);
  border-radius: var(--be-v3-radius, 12px);
  padding: 16px;
  margin-bottom: 20px;
}
.be-room-card__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
/* dims the page while a rate detail is expanded — the expanded row itself is lifted
   above it by .be-room-card__rate.is-expanded */
/* mobile keeps the row inline in the page flow (no raised white card to contrast
   against), so a full-screen dim there just darkens content with nothing to lift */
.be-room-card__backdrop {
  display: none;
}
@media screen and (min-width: 992px) {
  .be-room-card__backdrop {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1050;
  }
}
.be-room-card__rates {
  background: #fff;
  border: 1px solid var(--be-v3-border);
  border-radius: var(--be-v3-radius, 12px);
  padding: 4px 16px;
  /* visible so the qty dropdown menu is not clipped on the last rate row */
  overflow: visible;
}
.be-room-card__rates-head {
  display: none;
}
.be-room-card__showmore {
  text-align: center;
  border: 1px solid var(--be-v3-border);
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 10px;
  margin: 12px 0;
  font-weight: 600;
  font-size: var(--be-v3-text-body);
  cursor: pointer;
}
@media screen and (min-width: 992px) {
  .be-room-card__grid {
    /* minmax(0, 1fr) so the rates cell can actually shrink — a bare 1fr keeps its
       auto min-content width and overflows the card between 992px and 1400px */
    grid-template-columns: clamp(190px, 20vw, 260px) minmax(0, 1fr);
    /* keep each column at its own content height so a tall sidebar doesn't
       stretch the rates cell (and its column dividers) into empty space */
    align-items: start;
  }
  .be-room-card__rates {
    padding: 0;
  }
  .be-room-card__rates-head {
    display: grid;
    grid-template-columns: var(--be-v3-rate-grid);
    background: #eef0f4;
    border-bottom: 1px solid var(--be-v3-border);
    border-radius: var(--be-v3-radius, 12px) var(--be-v3-radius, 12px) 0 0;
    font-weight: 800;
    /* stroke so the weight bump is visible even with a single-file merchant font */
    -webkit-text-stroke: 0.25px currentColor;
    color: #1a1a2e;
    font-size: var(--be-v3-text-caption);
    line-height: 1.4;
  }
  .be-room-card__rates-head > div {
    padding: 18px 12px;
    min-width: 0;
  }
  .be-room-card__rates-head > div + div {
    border-left: 1px solid var(--be-v3-border);
  }
  .be-room-card__showmore {
    margin: 12px 16px;
  }
}
/* Jost (the v3 default content font) is a real variable font that already gets
   heavier at font-weight 800 — the stroke above was tuned for BeV3Sans/merchant
   single-file fonts and doubles up on Jost, so drop it when no custom font is set. */
.be-v3-content-font-default .be-room-card__rates-head {
  -webkit-text-stroke: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeRoomCard.vue